/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {ApiKeyStatus, ApiKeyStatusMap} from '../util/interfaces';

import {ModelResponse, ResponseData} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import Account from './accountModel';

import Invoice from './invoiceModel';

import {activateApiKey, deactivateApiKey, getApiKeys, updateAPIKey} from '../util/requestSender';

import {deepClone} from '../util/utils';

import {APIType} from '../util/enums';

export const IGNORE_EXPIRY_FLEX_KEY = 'ignore_expiry';

interface Props extends BaseProps {
    label?: string;
    key?: string;
    secret?: string;
    status?: ApiKeyStatus;
    error?: string | null;
    send_history?: boolean;
    history_sent?: boolean;
    importing_products?: boolean;
    num_importing_products?: number;
    invoice_count?: number;
    number?: number | null; // Used for the user to identify the connection between invoice and api key
    account_id?: string;
    account?: Account | any;
    invoices?: (Invoice | any)[];
    importing_returns?: boolean;
    use_oauth?: boolean;
    refresh_token_expires_at?: string | null;
    seller_id?: string;
    active_since?: string;
    api_types?: Array<APIType>;
    flex?: Record<string, any>;
}

interface Fields extends BaseFields {
    label: string;
    key: string;
    secret: string;
    status: ApiKeyStatus;
    error: string | null;
    send_history: boolean;
    history_sent: boolean;
    importing_products: boolean;
    num_importing_products: number;
    invoice_count: number;
    number: number | null;
    account_id: string;
    account: Account | null;
    invoices: Invoice[];
    importing_returns: boolean;
    use_oauth: boolean;
    refresh_token_expires_at: string | null;
    seller_id: string;
    active_since: string;
    api_types: Array<APIType>;
    flex: Record<string, any>;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    label: '',
    key: '',
    secret: '',
    status: ApiKeyStatus.ACTIVE,
    error: '',
    send_history: true,
    history_sent: true,
    importing_products: true,
    invoice_count: 0,
    num_importing_products: 0,
    account_id: '',
    account: null,
    invoices: new Array<Invoice>(),
    number: null,
    importing_returns: false,
    use_oauth: true,
    refresh_token_expires_at: null,
    seller_id: '',
    active_since: '',
    api_types: [APIType.RETAILER],
    flex: {}
};

const fieldsDefinition: {[key in keyof Omit<Fields, 'account' | 'invoices'>]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    label: createFieldDef(ModelFieldType.STRING),
    key: createFieldDef(ModelFieldType.STRING),
    secret: createFieldDef(ModelFieldType.STRING),
    error: createFieldDef(ModelFieldType.STRING),
    send_history: createFieldDef(ModelFieldType.BOOLEAN),
    history_sent: createFieldDef(ModelFieldType.BOOLEAN),
    importing_products: createFieldDef(ModelFieldType.BOOLEAN),
    num_importing_products: createFieldDef(ModelFieldType.NUMBER),
    account_id: createFieldDef(ModelFieldType.STRING),
    status: createFieldDef(ModelFieldType.STRING, ApiKeyStatus.ACTIVE),
    invoice_count: createFieldDef(ModelFieldType.NUMBER),
    number: createFieldDef(ModelFieldType.NUMBER, null, true),
    importing_returns: createFieldDef(ModelFieldType.BOOLEAN),
    use_oauth: createFieldDef(ModelFieldType.BOOLEAN),
    refresh_token_expires_at: createFieldDef(ModelFieldType.STRING, null, true),
    seller_id: createFieldDef(ModelFieldType.STRING),
    active_since: createFieldDef(ModelFieldType.STRING),
    api_types: createFieldDef(ModelFieldType.ARRAY, [APIType.RETAILER]),
    flex: createFieldDef(ModelFieldType.OBJECT)
};

const ignoreProps: Array<keyof Props> = ['account', 'invoices'];

export default class ApiKey extends BaseModel<Props, Fields> {
    static getAll = (accountId: string, callback: ModelResponse<ApiKey[]>) => {
        getApiKeys({
            account_id: accountId,
            callback: (data: ResponseData) => {
                const apiKeys: ApiKey[] = data.success ? data.api_keys.map((key: any) => new ApiKey(key)) : [];
                callback(apiKeys, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get label(): string {
        return this.fields.label;
    }

    set label(label: string) {
        this.fields.label = label;
    }

    get key(): string {
        return this.fields.key;
    }

    set key(key: string) {
        this.fields.key = key;
    }

    get secret(): string {
        return this.fields.secret;
    }

    set secret(secret: string) {
        this.fields.secret = secret;
    }

    get status(): ApiKeyStatus {
        return this.fields.status;
    }

    get translatedStatus(): string {
        return ApiKeyStatusMap[this.status];
    }

    set status(status: ApiKeyStatus) {
        this.fields.status = status;
    }

    get error(): string | null {
        return this.fields.error;
    }

    set error(error: string | null) {
        this.fields.error = error;
    }

    get sendHistory(): boolean {
        return this.fields.send_history;
    }

    set sendHistory(send_history: boolean) {
        this.fields.send_history = send_history;
    }

    get historySent(): boolean {
        return this.fields.history_sent;
    }

    set historySent(history_sent: boolean) {
        this.fields.history_sent = history_sent;
    }

    get importingProducts(): boolean {
        return this.fields.importing_products;
    }

    set importingProducts(importing_products: boolean) {
        this.fields.importing_products = importing_products;
    }

    get numImportingProducts(): number {
        return this.fields.num_importing_products;
    }

    set numImportingProducts(num_importing_products: number) {
        this.fields.num_importing_products = num_importing_products;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get account(): Account | null {
        return this.fields.account;
    }

    set account(account: Account | null) {
        this.fields.account = account;
        if (account) this.fields.account_id = account.id;
    }

    get invoices(): Invoice[] {
        return this.fields.invoices;
    }

    set invoices(invoices: Invoice[]) {
        this.fields.invoices = invoices;
    }

    get invoiceCount(): number {
        return this.fields.invoice_count;
    }

    get number(): number | null {
        return this.fields.number;
    }

    get importingReturns(): boolean {
        return this.fields.importing_returns;
    }

    get useOAuth(): boolean {
        return this.fields.use_oauth;
    }

    get refreshExpiresAt(): IDate | null {
        if (this.fields.refresh_token_expires_at) return new IDate(this.fields.refresh_token_expires_at);
        return null;
    }

    get sellerID(): string {
        return this.fields.seller_id;
    }

    get activeSince(): string {
        return this.fields.active_since;
    }

    get activeSinceMoment(): IDate | null {
        if (!this.activeSince) return null;
        return new IDate(this.activeSince);
    }

    get isActive(): boolean {
        return this.status === ApiKeyStatus.IMPORTING || this.status === ApiKeyStatus.ACTIVE;
    }

    get flex(): Record<string, any> {
        return this.fields.flex;
    }

    set flex(value: Record<string, any>) {
        this.fields.flex = value;
    }

    get apiTypes(): Array<APIType> {
        return this.fields.api_types;
    }

    set apiTypes(value: Array<APIType>) {
        this.fields.api_types = value;
    }

    enabledForAPIType = (api_type: APIType): boolean => {
        return this.apiTypes.includes(api_type);
    };

    getFlex = <T>(key: string): T | undefined => {
        return this.flex[key];
    };

    setFlex = (key: string, value: any) => {
        this.flex[key] = value;
    };

    clone = (deep: boolean = true): ApiKey => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new ApiKey(fields);
    };

    activate = (callback: ModelResponse<ApiKey>) => {
        activateApiKey({
            data: {account_id: this.accountId, api_key_id: this.id},
            callback: (data: ResponseData) => {
                if (data.success) {
                    this.build(data.api_key);
                    this.account?.replaceApiKey(this);
                }
                callback(this, data.success, data);
            }
        });
    };

    deactivate = (callback: ModelResponse<ApiKey>) => {
        deactivateApiKey({
            account_id: this.accountId,
            api_key_id: this.id,
            callback: (data: ResponseData) => {
                if (data.success) {
                    this.build(data.api_key);
                    this.account?.replaceApiKey(this);
                }
                callback(this, data.success, data);
            }
        });
    };

    update = (callback: ModelResponse<ApiKey>) => {
        return updateAPIKey({
            data: {flex: this.flex, api_key_id: this.id, account_id: this.accountId},
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.api_key);
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.account)
                this.account = props.account instanceof Account ? props.account : new Account(props.account);
            if (props.invoices) {
                const invoices: Invoice[] = [];
                for (const inv of props.invoices) {
                    const invoice: Invoice = inv instanceof Invoice ? inv : new Invoice(inv);
                    invoice.apiKey = this;
                    invoices.push(invoice);
                }
                this.fields.invoices = invoices;
            }
        }
    };
}
